import { computePosition, flip, shift, offset } from "@floating-ui/dom"
import { escapeHTML } from "../helpers"

const TYPES = ["tooltip", "popover"]
const tooltips = new WeakMap()

export class Tooltip {
  static install(element, options = {}) {
    this.uninstall(element)
    const tooltip = new this(element, options)
    tooltip.install()
    tooltips.set(element, tooltip)
    return tooltip
  }

  static uninstall(element) {
    const tooltip = tooltips.get(element)
    if (!tooltip) return
    tooltip.uninstall()
    tooltips.delete(element)
  }

  constructor(element, { placement, text, html, target, type } = {}) {
    this.element = element
    this.options = { placement, text, html, target, type: TYPES.includes(type) ? type : TYPES[0] }
    this.id = `tooltip-${Math.random().toString(16).slice(2, 10)}`
  }

  install() {
    if (this.installed) return
    this.installed = true

    this.element.addEventListener("mouseenter", this.show)
    this.element.addEventListener("focusin", this.show)
    this.element.addEventListener("mouseleave", this.hide)
    this.element.addEventListener("focusout", this.hide)
  }

  uninstall() {
    if (!this.installed) return
    this.installed = false

    this.element.removeEventListener("mouseenter", this.show)
    this.element.removeEventListener("focusin", this.show)
    this.element.removeEventListener("mouseleave", this.hide)
    this.element.removeEventListener("focusout", this.hide)

    this.hide()
  }

  show = async () => {
    if (this.isVisible || !this.shouldShow) return
    const { element, tipElement, placement } = this

    const title = element.getAttribute("title")
    if (title) {
      element.removeAttribute("title")
      element.setAttribute("aria-label", title)
    }
    element.setAttribute("aria-describedby", this.id)

    document.body.append(tipElement)
    tipElement.showPopover()

    const middleware = [offset(8), flip(), shift({ padding: 5 })]
    const options = { middleware, placement }
    const { x, y } = await computePosition(element, tipElement, options)
    Object.assign(tipElement.style, { left: `${x}px`, top: `${y}px`, margin: 0 })
  }

  hide = () => {
    if (this.isHidden) return

    const { element, tipElement } = this

    tipElement.hidePopover()
    if (!this.options.target) tipElement.remove()

    const title = element.getAttribute("aria-label")
    if (title) {
      element.removeAttribute("aria-label")
      element.setAttribute("title", title)
    }
    element.removeAttribute("aria-describedby")
  }

  get type() {
    return this.options.type
  }

  get text() {
    return this.options.text || this.element.getAttribute("title") || this.element.getAttribute("aria-label") || ""
  }

  get html() {
    return this.options.html || this.text
  }

  get placement() {
    const value = this.options.placement || this.element.getAttribute("data-tooltip")
    return value && value != "true" ? value : "top"
  }

  get tipElement() {
    const element = this.options.target || createElement(this.tipHTML)
    const value = Object.assign(element, { popover: "manual", id: this.id })
    Object.defineProperty(this, "tipElement", { value })
    return value
  }

  get tipHTML() {
    const classes = `sh-${this.type} ${this.type} fade`
    const content =
      this.type === "popover"
        ? `<h3 class="popover-header"></h3><div class="sh-popover-content popover-body">${this.html}</div>`
        : `<div class="tooltip-inner">${escapeHTML(this.text)}</div>`

    return `<div class="${classes}" role="tooltip" id="${this.id}">${content}</div>`
  }

  get shouldShow() {
    return this.shouldOnlyShowWhenTruncated ? this.isTruncated : true
  }

  get shouldOnlyShowWhenTruncated() {
    return this.element.hasAttribute("data-tooltip-if-truncated")
  }

  get isTruncated() {
    return this.element.scrollWidth > this.element.clientWidth
  }

  get isVisible() {
    return this.tipElement && this.tipElement.matches(":popover-open")
  }

  get isHidden() {
    return !this.isVisible
  }
}

function createElement(html = "") {
  const template = document.createElement("template")
  template.innerHTML = html.trim()
  return template.content.firstElementChild
}
